import { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import type { EmployeeCompetency } from '@/types/manager-assessment/Competency';
import type { AssessmentEmployee } from '@/types/manager-assessment/Employee';
import type { AssessmentMethod } from '@/types/manager-assessment/Evaluation';
import type { Skill } from '@/types/manager-assessment/Skill';

import { useGetProficiencyLevels } from '@/api/competency-services';
import { useCreateCycleInstance } from '@/api/cycle-evaluation-services/cycle-instance';
import {
    useGetEmployeeCompetencies,
    useSaveManagerAssessment,
} from '@/api/cycle-evaluation-services/manager-assessment';
import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { useNavigate } from '@/hooks';
import { useError } from '@/hooks/utils/useError';
import { RouteName } from '@/navigation/types';
import { orderProficiencyLevelsWorstFirst } from '@/utils/proficiencyLevel';

interface ManagerAssessmentSetupState {
    preselectedEmployeeId?: string;
}

const EMPTY_COMPETENCIES: EmployeeCompetency[] = [];
const MIN_WEAKEST_COUNT = 3;
const STALE_ASSESSMENT_MONTHS = 6;

/**
 * Rank of the weakest level once the scale is read worst-first. A competency that has never been
 * assessed carries this rank, so it joins the weakest level rather than forming a group of its own
 * (US #12742).
 */
const WEAKEST_RANK = 0;

const competenciesToSkills = (comps: EmployeeCompetency[]): Skill[] =>
    comps.map((c) => ({
        id: c.id,
        name: c.name,
        description: c.description ?? '',
        questions: c.questionCount,
    }));

export const useManagerAssessmentSetup = () => {
    const navigate = useNavigate();
    const { handleError } = useError();
    const location = useLocation<ManagerAssessmentSetupState>();
    const state = location.state;

    const { mutateAsync: createCycleInstance, isPending: isCreatingInstance } =
        useCreateCycleInstance();
    const { mutateAsync: saveManagerAssessment, isPending: isSavingAssessment } =
        useSaveManagerAssessment();
    const isSaving = isCreatingInstance || isSavingAssessment;

    const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
    const [selectedSkillOption, setSelectedSkillOption] = useState<string | null>(null);
    const [selectedMethod, setSelectedMethod] = useState<AssessmentMethod | null>(null);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
    const [selectedSkills, setSelectedSkills] = useState<Skill[]>([]);
    const [includeCompanyValues, setIncludeCompanyValues] = useState(false);
    const [isAddSkillsOpen, setIsAddSkillsOpen] = useState(false);
    const isStartingRef = useRef(false);

    const { data: competencies = EMPTY_COMPETENCIES, isLoading: isLoadingCompetencies } =
        useGetEmployeeCompetencies(selectedEmployeeId ?? undefined);
    // Load org-defined proficiency levels for weakest skills selection.
    const { data: proficiencyLevels = [] } = useGetProficiencyLevels();

    const weakestCompetencies = useMemo(() => {
        if (competencies.length === 0) return [];

        // The scale runs strongest-first -- sortOrder 0 is the top level, e.g. "Mastered" -- so
        // weakness is a rank in the shared worst-first order, never the raw sortOrder.
        const rankById = new Map(
            orderProficiencyLevelsWorstFirst(proficiencyLevels).map((level, rank) => [
                level.id ?? '',
                rank,
            ]),
        );

        const byRank = new Map<number, EmployeeCompetency[]>();
        competencies.forEach((comp) => {
            const rank = comp.lastAssessedProficiencyLevelId
                ? (rankById.get(comp.lastAssessedProficiencyLevelId) ?? WEAKEST_RANK)
                : WEAKEST_RANK;
            if (!byRank.has(rank)) {
                byRank.set(rank, []);
            }
            byRank.get(rank)!.push(comp);
        });

        // Take complete levels from the weakest up until we have at least MIN_WEAKEST_COUNT — never
        // splitting a level.
        const selected: EmployeeCompetency[] = [];
        for (const rank of Array.from(byRank.keys()).sort((a, b) => a - b)) {
            selected.push(...byRank.get(rank)!);
            if (selected.length >= MIN_WEAKEST_COUNT) {
                break;
            }
        }

        return selected;
    }, [competencies, proficiencyLevels]);

    const oldCompetencies = useMemo(() => {
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - STALE_ASSESSMENT_MONTHS);
        // A competency that has never been assessed has no age, so it is not "6+ months old"
        // and is left out entirely: a brand new employee gets an empty preset rather than their
        // whole profile (#14502). Weakest skills deliberately does the opposite and counts the
        // unassessed as the weakest level, per US #12742.
        return competencies.filter(
            (c) => c.lastAssessedAt && new Date(c.lastAssessedAt) < sixMonthsAgo,
        );
    }, [competencies]);

    const skillCounts = useMemo(
        () => ({
            all: competencies.length,
            weakest: weakestCompetencies.length,
            old: oldCompetencies.length,
        }),
        [competencies.length, weakestCompetencies.length, oldCompetencies.length],
    );

    const isSection2Unlocked = selectedEmployeeId !== null;
    const hasNoCompetencies =
        isSection2Unlocked && !isLoadingCompetencies && competencies.length === 0;
    const isSection3TemplateUnlocked = selectedMethod === 'template';
    const isSection3ReviewUnlocked =
        !isSection3TemplateUnlocked &&
        ((selectedMethod === 'predefined' && selectedSkillOption !== null) ||
            selectedMethod === 'custom');
    const isTemplateFlowComplete = selectedMethod === 'template' && selectedTemplateId !== null;
    const isSection4Unlocked = isSection3ReviewUnlocked || isTemplateFlowComplete;

    /**
     * The one rule for "this assessment can be started": both Start buttons and the handler's own
     * guard read it, so they cannot drift apart. A template flow needs no competency of its own --
     * the template carries the rule -- every other flow needs at least one, and nothing starts while
     * a save is already on its way.
     */
    const canStartAssessment =
        selectedEmployeeId !== null &&
        (isTemplateFlowComplete || selectedSkills.length > 0) &&
        !isSaving;

    const resetSelections = useCallback(() => {
        setSelectedSkills([]);
        setSelectedSkillOption(null);
        setSelectedMethod(null);
        setSelectedTemplateId(null);
    }, []);

    const handleEmployeeSelect = useCallback(
        (employee: AssessmentEmployee) => {
            setSelectedEmployeeId(employee.id);
            resetSelections();
        },
        [resetSelections],
    );

    const handleDeselectEmployee = useCallback(() => {
        setSelectedEmployeeId(null);
        resetSelections();
    }, [resetSelections]);

    const handleSkillOptionSelect = useCallback(
        (optionId: 'all' | 'weakest' | 'old') => {
            setSelectedSkillOption(optionId);
            setSelectedMethod('predefined');
            setSelectedTemplateId(null);

            const competencyMap: Record<string, EmployeeCompetency[]> = {
                all: competencies,
                weakest: weakestCompetencies,
                old: oldCompetencies,
            };
            setSelectedSkills(competenciesToSkills(competencyMap[optionId]));
        },
        [competencies, weakestCompetencies, oldCompetencies],
    );

    const handleMethodSelect = useCallback((method: 'custom' | 'template') => {
        setSelectedMethod(method);
        setSelectedSkillOption(null);
        setSelectedTemplateId(null);
        // Both methods start from an empty selection. A template carries its own rule for which
        // competencies to assess, and the server only reads that rule when the request carries none
        // of its own -- a preset left over from an abandoned choice would silently take its place,
        // while this screen no longer shows it.
        setSelectedSkills([]);
    }, []);

    const handleRemoveSkill = useCallback((skillId: string) => {
        setSelectedSkills((prev) => prev.filter((s) => s.id !== skillId));
    }, []);

    const handleOpenAddSkills = useCallback(() => {
        setIsAddSkillsOpen(true);
    }, []);

    const handleCloseAddSkills = useCallback(() => {
        setIsAddSkillsOpen(false);
    }, []);

    const handleConfirmAddSkills = useCallback(
        (skillIds: string[]) => {
            const newSkills = skillIds
                .filter((id) => !selectedSkills.some((s) => s.id === id))
                .map((id) => {
                    const comp = competencies.find((c) => c.id === id);
                    return {
                        id,
                        name: comp?.name ?? '',
                        description: comp?.description ?? '',
                        questions: comp?.questionCount ?? 0,
                    };
                });
            setSelectedSkills((prev) => [...prev, ...newSkills]);
        },
        [selectedSkills, competencies],
    );

    const handleStartAssessment = useCallback(async () => {
        // A ref rather than `isSaving` alone: two clicks in the same frame both read the render they
        // came from, where nothing is pending yet, and the second would create a second
        // CycleInstance that nothing ever uses.
        if (isStartingRef.current || !canStartAssessment) {
            return;
        }

        isStartingRef.current = true;

        try {
            // Create the CycleInstance only now — clicking the menu item earlier does not,
            // so no empty/unassigned instances are left behind.
            const cycleInstance = await createCycleInstance(EvaluationType.MANAGER_EVALUATION);

            // Persist the setup onto the new CycleInstance (Steps 1–5).
            await saveManagerAssessment({
                cycleInstanceId: cycleInstance.id,
                payload: {
                    assesseeId: selectedEmployeeId,
                    competencies: selectedSkills.map((skill) => ({ competencyId: skill.id })),
                    includeCompanyValues,
                    managerTemplateId: selectedTemplateId,
                },
            });

            // The cycleInstanceId is in the URL so a refresh keeps the context (state is a hint only).
            navigate({
                path: `${RouteName.ManagerAssessmentEvaluation}/${cycleInstance.id}`,
                state: {
                    cycleInstanceId: cycleInstance.id,
                    employeeId: selectedEmployeeId,
                    competencyIds: selectedSkills.map((s) => s.id),
                    isNewEvaluation: true,
                    includeCompanyValues,
                    templateId: selectedTemplateId,
                },
            });
        } catch (error) {
            handleError({ error });
        } finally {
            isStartingRef.current = false;
        }
    }, [
        canStartAssessment,
        selectedEmployeeId,
        selectedSkills,
        includeCompanyValues,
        selectedTemplateId,
        navigate,
        createCycleInstance,
        saveManagerAssessment,
        handleError,
    ]);

    return {
        competencies,
        isLoadingCompetencies,
        selectedEmployeeId,
        preselectedEmployeeId: state?.preselectedEmployeeId,
        selectedSkillOption,
        selectedMethod,
        selectedTemplateId,
        selectedSkills,
        includeCompanyValues,
        skillCounts,
        isSection2Unlocked,
        hasNoCompetencies,
        isSection3TemplateUnlocked,
        isSection3ReviewUnlocked,
        isTemplateFlowComplete,
        isSection4Unlocked,
        isAddSkillsOpen,
        canStartAssessment,
        setSelectedTemplateId,
        setIncludeCompanyValues,
        handleEmployeeSelect,
        handleDeselectEmployee,
        handleSkillOptionSelect,
        handleMethodSelect,
        handleRemoveSkill,
        handleOpenAddSkills,
        handleCloseAddSkills,
        handleConfirmAddSkills,
        handleStartAssessment,
    };
};
